import React, { useState, useEffect } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";

import { Building, CalendarClock, HandPlatter, Trash2 } from 'lucide-react';

import { motion } from "framer-motion";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Lottie from 'lottie-react';
import { toast } from 'react-toastify';
import moment from 'moment';

import TopBar from 'core/widgets/ui/TopBar';

import ServiceFloor from 'services/serviceFloor';

import animBtnLoading from 'assets/anim/anim-btnLoading.json';
import animPage from 'assets/anim/anim-pageload.json';

export default function PanelSettingsRoomType(props) {

    const [types, setTypes] = useState([]);

    const [selRoomType, setSelRoomType] = useState({});

    const [isOpen, setIsOpen] = useState(false);
    const [isOpenB, setIsOpenB] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [loading, setLoading] = useState(true);

    const floorService = new ServiceFloor();

    const priceTypes = [
        { value: 'split', label: 'Split' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'individual', label: 'Individual' },
    ];

    const formVSchema = Yup.object().shape({
        name: Yup.string().required('This information is required'),
        checkInTime: Yup.string().required('This information is required'),
        checkOutTime: Yup.string().required('This information is required'),
        maxCapacity: Yup.number().required('This information is required'),
        extraCapacity: Yup.number().required('This information is required'),
        priceVariationType: Yup.string().required('This information is required'),
        extraPersonCharge: Yup.number().required('This information is required'),
    });

    const { values, errors, touched, handleChange, handleSubmit, handleReset, setValues, resetForm } = useFormik({
        initialValues: {
            name: '',
            description: '',
            checkInTime: '',
            checkOutTime: '',
            maxCapacity: 0,
            extraCapacity: 0,
            priceVariationType: '',
            pricing: {
                "fixed": 0,
                "split": {
                    "weekday": 0,
                    "weekend": 0
                },
                "individual": {
                    "mon": 0,
                    "tue": 0,
                    "wed": 0,
                    "thu": 0,
                    "fri": 0,
                    "sat": 0,
                    "sun": 0
                }
            },
            extraPersonCharge: 0,
        },
        enableReinitialze: true,
        validationSchema: formVSchema,
        onSubmit: values => {
            setSubmit(true);
            let body = {
                "name": values.name,
                "description": values.description,
                "checkInTime": values.checkInTime,
                "checkOutTime": values.checkOutTime,
                "maxCapacity": values.maxCapacity,
                "extraCapacity": values.extraCapacity,
                "priceVariationType": values.priceVariationType,
                "pricing": values.pricing,
                "extraPersonCharge": values.extraPersonCharge
            }
            floorService.addRoomType(body, '').then((res) => {
                setSubmit(false);
                if (res.status) {
                    toast.success("New room type added!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    setIsOpen(false);
                    resetForm();
                    handleReset();
                    loadData();
                } else {
                    toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                }
            });
        }
    });

    const deleteRoom = (doc) => {
        let body = {
            did: doc._id,
        }
        floorService.deleteRoomType(body, '').then((res) => {
            if (res.status) {
                toast.success("Room Type Deleted!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                handleReset();
                loadData();
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        });
    }

    const loadData = () => {
        (async function () {
            var res = await floorService.getAllRoomType({}, '');
            setTypes(res.data);
            setLoading(false);
        })();
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <>
            {
                loading && <div className='flex items-center justify-center w-full h-full'>
                    <Lottie animationData={animPage} className="w-40 h-40" loop={true} />
                </div>
            }
            {
                isOpen && <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-[70%] min-h-screen absolute right-0 p-[20px]'>
                        <ScrollArea>
                            <div className='px-2'>
                                <div className='flex items-center justify-between text-2xl'>
                                    <h1 className='font-sans font-medium text-gray-900 uppercase'>{isEdit ? "UPDATE" : "ADD"} ROOM TYPE</h1>
                                    <span onClick={() => {
                                        setIsOpen(false);
                                        resetForm();
                                        handleReset();
                                        loadData();
                                    }}><i className="las la-times text-xl text-gray-700 cursor-pointer"></i></span>
                                </div>
                                <hr className='mt-2' />
                                <form className='mt-4 grid grid-cols-12 gap-4' onSubmit={handleSubmit} onReset={handleReset} noValidate="" autoComplete='off'>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Room Type Name<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="name" value={values.name} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        {(errors.name && touched.name) && <p className='text-xs text-red-400 mt-1'>{errors.name}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Check-In Time<sup className="text-red-600">*</sup></label>
                                        <input type="time" id="checkInTime" value={values.checkInTime} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        {(errors.checkInTime && touched.checkInTime) && <p className='text-xs text-red-400 mt-1'>{errors.checkInTime}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Check-Out Time<sup className="text-red-600">*</sup></label>
                                        <input type="time" id="checkOutTime" value={values.checkOutTime} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        {(errors.checkOutTime && touched.checkOutTime) && <p className='text-xs text-red-400 mt-1'>{errors.checkOutTime}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Room Max Capacity<sup className="text-red-600">*</sup></label>
                                        <input type="number" id="maxCapacity" value={values.maxCapacity} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        {(errors.maxCapacity && touched.maxCapacity) && <p className='text-xs text-red-400 mt-1'>{errors.maxCapacity}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Room Extra Capacity<sup className="text-red-600">*</sup></label>
                                        <input type="number" id="extraCapacity" value={values.extraCapacity} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        {(errors.extraCapacity && touched.extraCapacity) && <p className='text-xs text-red-400 mt-1'>{errors.extraCapacity}</p>}
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Room Type Description</label>
                                        <textarea type="text" id="description" value={values.description} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        {(errors.description && touched.description) && <p className='text-xs text-red-400 mt-1'>{errors.description}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">Room Type Pricing<sup className="text-red-600">*</sup></label>
                                        <select id="priceVariationType" className="border-gray-300 border rounded appearance-none w-full py-1 px-2 bg-white mt-2" onChange={handleChange} value={values.priceVariationType}>
                                            <option value="" disabled="" selected="selected">Select</option>
                                            {
                                                priceTypes.length !== 0 && priceTypes.map((item) => {
                                                    return <option value={item.value} selected={values.priceVariationType == item.value}>{item.label}</option>
                                                })
                                            }
                                        </select>
                                        {(errors.priceVariationType && touched.priceVariationType) && <p className='text-xs text-red-400 mt-1'>{errors.priceVariationType}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Extra Person Charges<sup className="text-red-600">*</sup></label>
                                        <input type="number" id="extraPersonCharge" value={values.extraPersonCharge} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        {(errors.extraPersonCharge && touched.extraPersonCharge) && <p className='text-xs text-red-400 mt-1'>{errors.extraPersonCharge}</p>}
                                    </div>

                                    {
                                        values.priceVariationType === "fixed" && <div className="col-span-4">
                                            <label className="block mb-2 text-sm font-medium text-gray-600">Fixed Price<sup className="text-red-600">*</sup></label>
                                            <input type="number" id="pricing-fixed" name="pricing.fixed" value={values.pricing.fixed} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                        </div>
                                    }

                                    {
                                        values.priceVariationType === "split" && <>
                                            <div className="col-span-4"></div>
                                            <div className="col-span-4">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Week Day Price<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-split-weekday" name="pricing.split.weekday" value={values.pricing.split.weekday} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                            <div className="col-span-4">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Weekend Price<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-split-weekend" name="pricing.split.weekend" value={values.pricing.split.weekend} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                        </>
                                    }

                                    {
                                        values.priceVariationType === "individual" && <>
                                            <div className="col-span-4"></div>
                                            <div className="">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Monday<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-individual-mon" name="pricing.individual.mon" value={values.pricing.individual.mon} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                            <div className="">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Tuesday<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-individual-tue" name="pricing.individual.tue" value={values.pricing.individual.tue} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                            <div className="">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Wednesday<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-individual-wed" name="pricing.individual.wed" value={values.pricing.individual.wed} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                            <div className="">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Thursday<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-individual-thu" name="pricing.individual.thu" value={values.pricing.individual.thu} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                            <div className="">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Friday<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-individual-fri" name="pricing.individual.fri" value={values.pricing.individual.fri} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                            <div className="">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Saturday<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-individual-sat" name="pricing.individual.sat" value={values.pricing.individual.sat} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                            <div className="">
                                                <label className="block mb-2 text-sm font-medium text-gray-600">Sunday<sup className="text-red-600">*</sup></label>
                                                <input type="number" id="pricing-individual-sun" name="pricing.individual.sun" value={values.pricing.individual.sun} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={handleChange} />
                                            </div>
                                        </>
                                    }

                                    <div className='col-span-12 my-6'>
                                        <div className='flex items-end'>
                                            <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                                {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                                {!submit && "SAVE"}
                                            </button>
                                        </div>
                                    </div>

                                </form>
                            </div>
                        </ScrollArea >
                    </div >
                </div >
            }
            {
                isOpenB && <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-[600px] min-h-screen absolute right-0 py-[12px] px-[8px]'>
                        <ScrollArea>
                            <div className='px-4'>
                                <div className='flex items-center justify-between text-2xl'>
                                    <h1 className='font-sans font-medium text-gray-900 uppercase'>Room Type</h1>
                                    <span onClick={() => {
                                        setIsOpenB(false);
                                        setSelRoomType({});
                                        loadData();
                                    }}><i className="las la-times text-xl text-gray-700 cursor-pointer"></i></span>
                                </div>
                                <hr className='my-2' />
                                <h1 className='text-xl font-sans font-medium text-gray-900 uppercase'>{selRoomType.name}</h1>
                                <div className='text-gray-700 flex items-center space-x-4 my-2'>
                                    <h1 className='font-sans font-medium'>Check In Time: {moment(selRoomType.checkInTime, "HH:mm").format("hh:mm A")}</h1>
                                    <h1 className='font-sans font-medium'>Check Out Time: {moment(selRoomType.checkOutTime, "HH:mm").format("hh:mm A")}</h1>
                                </div>
                                <div className='text-gray-700 flex items-center space-x-4 my-2'>
                                    <h1 className='font-sans font-medium'>Max Capacity: {selRoomType.maxCapacity} Person</h1>
                                    <h1 className='font-sans font-medium'>Extra Capacity: {selRoomType.extraCapacity} Person</h1>
                                </div>
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-4 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-rupee-sign mr-2 text-xl"></i>Room Rates - Fixed
                                    </span>
                                </div>
                                <ul className='text-gray-800 my-2 space-y-1 w-1/2'>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Fixed Rate:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.fixed}</span></div>
                                    </li>
                                </ul>
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-4 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-rupee-sign mr-2 text-xl"></i>Room Rates - Split
                                    </span>
                                </div>
                                <ul className='text-gray-800 my-2 space-y-1 w-1/2'>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Monday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.split.weekday}</span></div>
                                    </li>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Tuesday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.split.weekend}</span></div>
                                    </li>
                                </ul>
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-4 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-rupee-sign mr-2 text-xl"></i>Room Rates - Individual
                                    </span>
                                </div>
                                <ul className='text-gray-800 my-2 space-y-1 w-1/2'>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Monday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.individual.mon}</span></div>
                                    </li>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Tuesday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.individual.tue}</span></div>
                                    </li>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Wednesday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.individual.wed}</span></div>
                                    </li>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Thursday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.individual.thu}</span></div>
                                    </li>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Friday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.individual.fri}</span></div>
                                    </li>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Saturday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.individual.sat}</span></div>
                                    </li>
                                    <li className='flex items-center justify-between'>
                                        <h1 className='font-sans font-medium'>Sunday:</h1>
                                        <div><span className='text-green-600'>₹ {selRoomType.pricing.individual.sun}</span></div>
                                    </li>
                                </ul>
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-4 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-rupee-sign mr-2 text-xl"></i>Room Rates - Addtional
                                    </span>
                                </div>
                                <div className='text-gray-700 flex items-center space-x-4 my-2'>
                                    <h1 className='font-sans font-medium'>Addtional Charges <span className='text-green-600'>{selRoomType.extraPersonCharge} ₹</span> Per Person</h1>
                                </div>
                            </div>
                        </ScrollArea >
                    </div >
                </div >
            }
            <ScrollArea className="w-full h-full py-2 px-2">
                <div className="grid grid-cols-12 grid-gap-2">
                    <div className="col-span-12 mt-2">
                        <div className="intro-y flex items-center justify-between h-10 border-b pb-4">
                            <h2 className="text-2xl font-medium truncate ml-2">
                                <motion.i whileHover={{ scale: 1.2 }} className="las la-bars cursor-pointer" onClick={() => { props.onToggle(); }}></motion.i> Room Type Settings
                            </h2>
                            <TopBar />
                        </div>
                        <div className="w-full">
                            <div className="p-2 mt-2">
                                <div className='w-full min-h-screen bg-gray-100 rounded-md grid grid-cols-12 gap-2 p-2'>
                                    <div className='col-span-2 h-full border-r p-2'>
                                        <button className="w-full h-10 py-1 px-3 rounded-md bg-prime hover:bg-primeLight text-white text-sm" onClick={() => { setIsOpen(true); }}><i className="las la-plus mr-1"></i> ADD ROOM TYPE</button>
                                    </div>
                                    <div className='col-span-10'>
                                        <div className='w-full my-4 border border-gray-200 border-dashed p-1 rounded'>
                                            <div className='w-full'>
                                                <div className='flex items-center justify-between'>
                                                    <div className='flex items-center'>
                                                        <Building size={22} />
                                                        <h1 className='text-xl font-medium uppercase ml-1'>Room Types</h1>
                                                    </div>
                                                </div>
                                                <div className='my-2 h-[1px] bg-gray-300'></div>
                                            </div>
                                            <div className='w-full grid grid-cols-6 gap-2'>
                                                {
                                                    types.length === 0 && < div className='h-[114px] p-2 rounded shadow col-span-6 border border-dashed border-prime cursor-pointer hover:shadow-md flex items-center justify-center'>
                                                        <h1 className='text-sm font-medium text-prime'>No data found.</h1>
                                                    </div>
                                                }
                                                {
                                                    types.length === 0 !== 0 && types.map((type) => {
                                                        return <div className="h-[140px] p-2 bg-white rounded shadow col-span-2 border border-dashed border-gray-400 cursor-pointer hover:shadow-md flex flex-col items-start" key={type._id} onClick={() => {
                                                            setSelRoomType(type);
                                                            setIsOpenB(true);
                                                        }}>
                                                            <div className='w-full flex justify-between items-center px-1'>
                                                                <p className='text-xl font-medium font-serif'>{type.name}</p>
                                                                <div className='flex items-center'>
                                                                    <CalendarClock size={18} />
                                                                </div>
                                                            </div>
                                                            <div className='w-full bg-gray-200 h-[1px] my-2'></div>
                                                            <p className='text-sm font-medium font-serif'>Check In Time: {moment(type.checkInTime, "HH:mm").format("hh:mm A")}</p>
                                                            <p className='text-sm font-medium font-serif'>Check Out Time: {moment(type.checkOutTime, "HH:mm").format("hh:mm A")}</p>
                                                            <div className='flex-grow'></div>
                                                            <div className='w-full flex items-center justify-between'>
                                                                <div className='text-gray-600 flex items-center'>
                                                                    <HandPlatter size={20} />
                                                                    <p className='text-base font-medium font-serif ml-1'>Price Variation: <span className='uppercase'>{type.priceVariationType}</span></p>
                                                                </div>
                                                                <Trash2 size={18} className='cursor-pointer' onClick={() => {
                                                                    deleteRoom(type);
                                                                }} />
                                                            </div>
                                                        </div>
                                                    })
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollArea >
        </>
    )
}
